import { Button, Card, H3, InputEmail } from "@sellar-io/components";
import { AuthError, signInWithEmailLink } from "firebase/auth";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { useLocalStorage } from "usehooks-ts";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { InputContainer } from "@sellar-io/components";
import { NavigationBarMenu } from "../../../components/navigation/NavigationBar";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";

export const PasswordlessSignin = () => {
  const [magicLinkEmail] = useLocalStorage("magicLinkEmail", undefined);
  const [onboardingContinueUrl, setOnboardingContinueUrl] = useLocalStorage(
    "onboardingContinueUrl",
    "",
  );
  const [error, setError] = useState("");

  const formHook = useForm<{
    email: string;
  }>({
    defaultValues: {
      email: magicLinkEmail,
    },
  });

  const [searchParams] = useSearchParams();

  const { auth, user } = useFirebaseUser();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(Boolean(magicLinkEmail));
  const automaticSignInEmailRef = useRef<string | undefined>(undefined);

  const completeSignIn = (data: { email: string }) => {
    const continueUrlString = searchParams.get("continueUrl");
    let continueUrl: URL | undefined;

    if (continueUrlString) {
      continueUrl = new URL(continueUrlString);
    }

    const jwtSearchParam = continueUrl?.searchParams.get("jwt");

    if (
      continueUrl?.pathname &&
      continueUrl.pathname !== "/jwt" &&
      !onboardingContinueUrl
    ) {
      // Preserve full URL including query parameters (e.g., storefrontLink)
      setOnboardingContinueUrl(continueUrl.pathname + continueUrl.search);
    }

    signInWithEmailLink(auth, data.email, window.location.href)
      .then(() => {
        if (!continueUrl?.pathname) {
          return navigate("/");
        }

        if (continueUrl.pathname === "/jwt" && jwtSearchParam) {
          return navigate(`/jwt?jwt=${jwtSearchParam}`);
        }
      })
      .catch((e: AuthError) => {
        if (e.code) {
          if (e.code === "auth/invalid-action-code") {
            setError("This link is invalid.");
            return;
          }
          if (e.code === "auth/expired-action-code") {
            setError("This login link has expired.");
            return;
          }
          if (e.code === "auth/invalid-email") {
            setError("This is not the email associated with this invite.");
            return;
          }
          console.log(e.code);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const completeAutomaticSignIn = useEffectEvent(completeSignIn);

  const onSubmit = (data: { email: string }) => {
    setLoading(true);
    setError("");
    completeSignIn(data);
  };

  useEffect(() => {
    if (user) {
      navigate("/");
      return;
    }
    if (magicLinkEmail && automaticSignInEmailRef.current !== magicLinkEmail) {
      automaticSignInEmailRef.current = magicLinkEmail;
      completeAutomaticSignIn({ email: magicLinkEmail });
    }
  }, [navigate, magicLinkEmail, user]);

  return (
    <>
      <NavigationBarMenu title="Log in via email" />
      {loading ? (
        <SpinnerLegacy />
      ) : (
        <Card className="m-auto my-4 max-w-[900px] bg-white p-4">
          <form
            className={"flex flex-col gap-4"}
            onSubmit={formHook.handleSubmit(onSubmit)}
          >
            <H3>Log in with email</H3>

            <InputContainer>
              <InputEmail
                {...formHook.register("email")}
                placeholder={"Email"}
              />
            </InputContainer>
            <Button type={"submit"}>Log in</Button>
            {error ? <Alert type={"error"} message={error} /> : null}
          </form>
        </Card>
      )}
    </>
  );
};
